import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { listUnifiedAuditTimeline, type AuditEntry } from "@/lib/admin-oversight.functions";
import { getIsAdmin } from "@/lib/payments.functions";
import { ReserveHeader } from "@/components/ReserveHeader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/_authenticated/admin/audit-timeline")({
  head: () => ({ meta: [{ title: "Audit Timeline — Admin" }] }),
  component: AuditTimelinePage,
  errorComponent: ({ error }) => <div className="p-8 text-sm text-destructive">{error.message}</div>,
  notFoundComponent: () => <div className="p-8">Not found.</div>,
});

const SOURCES = [
  { value: "", label: "All sources" },
  { value: "suspension", label: "Suspensions" },
  { value: "cancellation", label: "Cancellations" },
  { value: "tip_allocation", label: "Tips" },
  { value: "payout", label: "Payouts" },
  { value: "insurance", label: "Insurance" },
  { value: "rtw", label: "Right to work" },
  { value: "age", label: "Age verification" },
  { value: "chat_moderation", label: "Chat moderation" },
];

function AuditTimelinePage() {
  const navigate = useNavigate();
  const checkAdmin = useServerFn(getIsAdmin);
  const load = useServerFn(listUnifiedAuditTimeline);
  const [source, setSource] = useState("");
  const [actorId, setActorId] = useState("");
  const [targetId, setTargetId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);

  const run = async () => {
    setLoading(true);
    try {
      const res = (await load({
        data: {
          source: source || undefined,
          actorId: actorId || undefined,
          targetId: targetId || undefined,
          from: from || undefined,
          to: to || undefined,
          limit: 200,
        },
      })) as { entries: AuditEntry[] } | { error: string };
      if ("error" in res) { toast.error(res.error); return; }
      setEntries(res.entries);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to load.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      const a = await checkAdmin();
      if (!a.isAdmin) { navigate({ to: "/dashboard", replace: true }); return; }
      await run();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const grouped = useMemo(() => {
    const map = new Map<string, AuditEntry[]>();
    for (const e of entries) {
      const day = e.created_at.slice(0, 10);
      const arr = map.get(day) ?? [];
      arr.push(e);
      map.set(day, arr);
    }
    return Array.from(map.entries());
  }, [entries]);

  const exportCsv = () => {
    const header = ["created_at", "source", "action", "actor_user_id", "target_user_id", "details"];
    const lines = [header.join(",")];
    for (const e of entries) {
      const cells = [
        e.created_at, e.source, e.action,
        e.actor_user_id ?? "", e.target_user_id ?? "",
        JSON.stringify(e.details),
      ].map((c) => /[",\n]/.test(c) ? `"${c.replace(/"/g, '""')}"` : c);
      lines.push(cells.join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `audit-timeline-${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader role="admin" />
      <main className="mx-auto max-w-6xl px-6 py-10">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <p className="tracking-wide-luxury text-accent">Admin Oversight</p>
            <h1 className="mt-3 font-display text-3xl">Unified audit timeline</h1>
            <p className="mt-2 text-sm text-muted-foreground">Every admin and system action across suspensions, payments, safety and compliance — one feed.</p>
          </div>
          <div className="flex gap-2">
            <Link to="/admin" className="border border-border px-4 py-2 text-xs tracking-wide-luxury hover:border-accent">Console</Link>
            <Button variant="outline" onClick={exportCsv} disabled={!entries.length}>Export CSV</Button>
          </div>
        </div>
        <span className="gold-divider mt-4" />

        <div className="mt-6 grid gap-4 border border-border bg-card p-5 sm:grid-cols-5">
          <div>
            <Label>Source</Label>
            <select className="mt-1 h-10 w-full border border-border bg-background px-3 text-sm" value={source} onChange={(e) => setSource(e.target.value)}>
              {SOURCES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          <div>
            <Label>Actor user id</Label>
            <Input value={actorId} onChange={(e) => setActorId(e.target.value)} placeholder="uuid" />
          </div>
          <div>
            <Label>Target user id</Label>
            <Input value={targetId} onChange={(e) => setTargetId(e.target.value)} placeholder="uuid" />
          </div>
          <div>
            <Label>From</Label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </div>
          <div>
            <Label>To</Label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          <div className="sm:col-span-5">
            <Button onClick={run} disabled={loading}>{loading ? "Loading…" : "Apply filters"}</Button>
          </div>
        </div>

        {loading ? (
          <p className="mt-12 text-center text-sm text-muted-foreground">Loading…</p>
        ) : entries.length === 0 ? (
          <p className="mt-12 text-center text-sm text-muted-foreground">No audit entries match these filters.</p>
        ) : (
          <div className="mt-8 space-y-8">
            {grouped.map(([day, items]) => (
              <section key={day}>
                <h2 className="text-xs tracking-wide-luxury text-accent uppercase">{day}</h2>
                <ol className="mt-3 space-y-2">
                  {items.map((e) => (
                    <li key={e.id} className="border border-border bg-card p-4">
                      <div className="flex flex-wrap items-baseline gap-3">
                        <span className="text-[10px] tracking-wide-luxury uppercase border border-border px-2 py-1">{e.source}</span>
                        <span className="font-medium text-sm">{e.action || "(no action)"}</span>
                        <span className="text-xs text-muted-foreground ml-auto">{new Date(e.created_at).toLocaleTimeString()}</span>
                      </div>
                      <div className="mt-2 grid gap-1 text-xs text-muted-foreground sm:grid-cols-2">
                        <div>Actor: <span className="font-mono">{e.actor_user_id ?? "—"}</span></div>
                        <div>Target: <span className="font-mono">{e.target_user_id ?? "—"}</span></div>
                      </div>
                      {Object.keys(e.details).length > 0 && (
                        <pre className="mt-2 text-xs whitespace-pre-wrap font-mono text-foreground/80">{JSON.stringify(e.details, null, 2)}</pre>
                      )}
                    </li>
                  ))}
                </ol>
              </section>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
